<template>
  <nav
    class="fixed top-0 sm:bottom-0 left-0 z-[60] flex flex-col justify-between flex-none w-full min-h-[100dvh] bg-white sm:border-r sidebar border-slate-200 dark:border-zinc-700 dark:bg-zinc-900"
    :class="sidebarClasses"
  >
    <div>
      <Logo
        class="inline-block mx-3.5 mt-4 rounded focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none dark:text-white"
        :icon="isExpanded ? 'general.logoWithText' : 'general.logo'"
        :src="orgCustomLogoUrl"
        :href="logoRedirectUrl"
        @click="logoClick"
      />
      <MainRouteMenu test-id="sidebar" :is-expanded="isExpanded" :type="isLoggedIn ? SIDEBAR_TYPES.LOGGED_IN : SIDEBAR_TYPES.GUEST" />
      <BrandingContent>
        <GuestMenu v-if="!isLoggedIn" data-testid="sidebar-guest-menu" :is-expanded="isExpanded" />
      </BrandingContent>
    </div>
    <div>
      <BrandingContent>
        <CreateTeam v-if="isExpanded && !isLoggedIn" />
      </BrandingContent>
      <InviteMembers v-if="isExpanded && isLoggedIn" />
      <div class="flex items-center justify-between m-6 gap-x-1 gap-y-2" :class="isExpanded ? '' : 'flex-col-reverse'">
        <UserMenu v-if="isLoggedIn" :is-expanded="isExpanded" :name="name || email" :avatar="avatarUrl" />
        <BrandingContent>
          <Contact v-if="!isLoggedIn" :is-wide="isExpanded" />
        </BrandingContent>
        <DarkMode v-if="!isDashboardRoute" />
      </div>
    </div>

    <Toggle :custom-logo="!!orgCustomLogoUrl" />
  </nav>

  <button v-if="isMobile" class="absolute inline-block top-5 left-4 sm:hidden dark:text-zinc-300" @click="layoutStore.toggleExtendedSidebar()">
    <Icon name="general.hamburger" size="base" />
    <span class="sr-only">Toggle mobile menu</span>
  </button>

  <Logo
    class="absolute top-0 left-8 inline-block mx-3.5 mt-4 rounded focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none dark:text-white sm:hidden"
    icon="general.logoWithText"
    :src="orgCustomLogoUrl"
    :href="logoRedirectUrl"
    @click="logoClick"
  />
</template>

<script setup lang="ts">
import { onBeforeMount, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

import { SIDEBAR_TYPES } from '@/router';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useUserStore } from '@/store/useUserStore';

import CreateTeam from './CreateTeam.vue';
import InviteMembers from './InviteMembers.vue';
import Toggle from './Toggle.vue';
import Contact from './Contact.vue';
import DarkMode from '@/components/DarkMode.vue';
import Logo from './Logo.vue';
import GuestMenu from './menu/Guest.vue';
import MainRouteMenu from './menu/Main.vue';
import UserMenu from './menu/User.vue';

import { useMobileDetection } from '@/composables/useMobileDetection';
import { TrackEvents, useTrack } from '@/composables/useTrack';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useBranding } from '@/composables/useBranding';

import BrandingContent from '@/components/BrandingContent.vue';
import { Icon } from '@/storybook/icon';

const { track } = useTrack();
const route = useRoute();

const layoutStore = useLayoutStore();
const { isSidebarExpanded: isExpanded } = storeToRefs(layoutStore);

const { id } = storeToRefs(useViewerItemStore());
const { orgRedirectLogoUrl, orgCustomLogoUrl, hideBranding } = useBranding();
const userStore = useUserStore();
const { isLoggedIn, avatarUrl, name, email } = storeToRefs(userStore);

const { isMobile } = useMobileDetection();

const logoClick = () => track(TrackEvents.LOGO_CLICK, { viewer: 'copernicus', hashid: id?.value });
const logoLink = computed(() => (!isLoggedIn.value ? 'https://www.zight.com' : '/'));

const logoRedirectUrl = computed(() => (orgCustomLogoUrl.value && orgRedirectLogoUrl.value ? orgRedirectLogoUrl.value : logoLink.value));

// Hide theme toggle for dashboard routes
const isDashboardRoute = computed(() => route.path.startsWith('/dashboard-new'));

const sidebarClasses = computed(() => {
  if (isExpanded.value) {
    return isMobile.value ? 'flex' : 'sm:w-72';
  }

  return isMobile.value ? 'hidden sm:flex' : 'sm:w-16 overflow-x-visible';
});

onBeforeMount(() => {
  if (isMobile.value || hideBranding.value) {
    layoutStore.hideSidebar();
  }
});
</script>
